import { useNavigate } from "react-router-dom";
import { FileText, Zap, Shield, Send, ChevronRight, Check, ScanLine, Smartphone, Upload, Sparkles, Download, CircleCheckBig } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useUserTier } from "@/hooks/useUserTier";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AppHeader from "@/components/AppHeader";
import AppFooter from "@/components/AppFooter";
import { useEffectiveBetaMode } from "@/hooks/useEffectiveBetaMode";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tier: userTier } = useUserTier();
  const { effectiveBetaMode: BETA_MODE } = useEffectiveBetaMode();

  const { data: tiers } = useQuery({
    queryKey: ["subscription-tiers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_tiers")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });

  const handleCTA = () => {
    navigate(user ? "/invoice/new" : "/auth");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <AppHeader />

      <main>
      {/* Hero */}
      <section className="relative pt-20 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="container relative text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm text-primary mb-8 animate-fade-in">
            <Zap className="h-3.5 w-3.5" />
            Conform cu legea RO e-Factura
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 animate-fade-in" style={{ animationDelay: "0.1s" }}>
            Factura ta către ANAF în{" "}
            <span className="text-primary">30 de secunde</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-10 animate-fade-in" style={{ animationDelay: "0.2s" }}>
            Încarcă factura → o convertim în XML conform ANAF → o trimitem direct la SPV → primești răspunsul. Poți și descărca PDF-ul. Atât.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: "0.3s" }}>
            <Button size="lg" className="text-base font-semibold px-8" onClick={handleCTA}>
              {user ? "Încarcă o Factură" : "Începe Gratuit"} <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" className="text-base" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}>
              Vezi Prețurile
            </Button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-4 bg-secondary/30">
        <div className="container max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-center mb-4">⚙️ Cum Funcționează?</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-lg mx-auto">4 pași simpli — de la factură la răspuns ANAF.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "1", icon: Upload, title: "Încarcă factura", desc: "Încarcă un PDF sau o imagine cu factura. Datele se completează automat." },
              { step: "2", icon: FileText, title: "Conversie XML", desc: "Factura ta e convertită automat în formatul XML cerut de ANAF (RO_CIUS)." },
              { step: "3", icon: Send, title: "Trimite la ANAF", desc: "Cu un singur click, factura ajunge direct în sistemul SPV al ANAF-ului." },
              { step: "4", icon: CircleCheckBig, title: "Primești răspuns", desc: "În câteva secunde sau minute, vezi dacă factura a fost acceptată sau dacă trebuie corectată." },
            ].map((s, i) => (
              <div key={i} className="relative rounded-xl border border-border bg-card p-6 text-center hover:border-primary/50 transition-colors">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 h-7 w-7 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                  {s.step}
                </div>
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 mt-2">
                  <s.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-xl border border-primary/20 bg-primary/5 p-4 flex items-center justify-center gap-3 text-sm">
            <Download className="h-4 w-4 text-primary shrink-0" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Bonus:</strong> Poți oricând descărca factura și ca PDF.
            </span>
          </div>
        </div>
      </section>


      {/* Features */}
      <section className="py-20 px-4">
        <div className="container max-w-5xl">
          <h2 className="font-heading text-3xl font-bold text-center mb-4">💡 De ce EZ Factura?</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-lg mx-auto">Simplu, rapid și 100% conform — totul dintr-un singur loc.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { icon: ScanLine, title: "Scanare Automată", desc: "Încarcă factura și datele se completează singure." },
              { icon: Zap, title: "Ultra Rapid", desc: "Creezi și trimiți o factură în sub 30 de secunde." },
              { icon: Shield, title: "100% Conform", desc: "XML în formatul cerut de ANAF, generat și validat automat." },
              { icon: Send, title: "Trimite Direct", desc: "Upload direct în SPV, fără alt portal sau site." },
              { icon: FileText, title: "Export Contabil", desc: "Trimite automat facturile contabilului tău lunar." },
            ].map((f, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-6 hover:border-primary/50 transition-colors">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <f.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-heading font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 bg-secondary/30">
        <div className="container max-w-5xl">
          <h2 className="font-heading text-3xl font-bold text-center mb-4">💰 Prețuri Simple</h2>
          <p className="text-center text-muted-foreground mb-4">Fără surprize. Fără contracte.</p>
          {BETA_MODE && (
            <p className="text-center text-sm text-primary font-medium mb-12">
              🧪 În perioada beta, toate planurile sunt gratuite. Plățile vor fi activate ulterior.
            </p>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {tiers?.map((tier) => {
              const isFree = Number(tier.price_ron) === 0;
              const isActive = user && userTier?.id === tier.id;
              const isFreeActive = user && userTier && Number(userTier.price_ron) === 0;
              return (
                <div
                  key={tier.id}
                  className={`rounded-xl border p-6 flex flex-col relative ${
                    isActive
                      ? "border-primary bg-primary/5 ring-2 ring-primary/50"
                      : "border-border bg-card"
                  }`}
                >
                  {isActive && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      Planul Tău Activ
                    </span>
                  )}
                  <h3 className="font-heading text-xl font-bold">{tier.name}</h3>
                  <div className="mt-4 mb-6">
                    <span className="text-3xl font-extrabold font-heading">
                      {`${tier.price_ron} RON`}
                    </span>
                    {!isFree && (
                      <span className="text-muted-foreground text-sm">/lună</span>
                    )}
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {(tier.features as string[])?.map((f: string, fi: number) => (
                      <li key={fi} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={isActive ? "secondary" : "outline"}
                    className="w-full"
                    disabled={!!isActive || (BETA_MODE && !isFree)}
                    onClick={() => {
                      if (BETA_MODE && !isFree) return;
                      if (user) {
                        navigate("/select-plan?tier=" + tier.slug);
                      } else {
                        navigate("/auth?tier=" + tier.slug);
                      }
                    }}
                  >
                    {isActive
                      ? "✓ Activ"
                      : BETA_MODE && !isFree
                      ? "Indisponibil în Beta"
                      : isFree
                      ? (user && userTier && !isFreeActive ? "Schimbă la Gratuit" : "Începe Gratuit")
                      : "Alege Planul"}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4">
        <div className="container max-w-3xl">
          <h2 className="font-heading text-3xl font-bold text-center mb-4">❓ Întrebări Frecvente</h2>
          <p className="text-center text-muted-foreground mb-12">Tot ce trebuie să știi despre e-Factura și EZ Factura.</p>
          <div className="space-y-4">
            {[
              {
                q: "Ce este e-Factura și de ce am nevoie de ea?",
                a: "E-Factura este sistemul electronic obligatoriu prin care firmele din România trimit facturi către ANAF în format XML. Dacă ai firmă (SRL, PFA, II, IF), ești obligat prin lege să trimiți facturile în acest format."
              },
              {
                q: "Trebuie să știu să fac fișiere XML?",
                a: "Nu. EZ Factura generează automat fișierul XML în formatul cerut de ANAF (RO_CIUS). Tu doar completezi datele facturii sau încarci un document, iar noi ne ocupăm de restul."
              },
              {
                q: "Cum ajunge factura la ANAF?",
                a: "După ce creezi factura, apeși un singur buton și factura se trimite direct în sistemul SPV (Spațiul Privat Virtual) al ANAF-ului. De obicei primești răspunsul în câteva secunde sau minute. Dacă totul e în regulă, factura e acceptată și nu mai ai nimic de făcut. Dacă apare vreo problemă, vei vedea exact ce eroare a returnat ANAF-ul, ca să poți corecta și retrimite."
              },
              {
                q: "Pot să descarc factura și ca PDF?",
                a: "Da, dar atenție: PDF-ul generat de EZ Factura este doar o copie de lucru, nu un document fiscal oficial. Singurul document cu valoare legală este cel din sistemul SPV al ANAF-ului. PDF-ul e util pentru a vizualiza rapid datele facturii sau pentru a verifica informațiile înainte de trimitere."
              },
              {
                q: "Este sigur? Datele mele sunt protejate?",
                a: "Absolut. Toate datele sunt criptate și stocate în siguranță. Conexiunea cu ANAF se face prin canale securizate, iar noi nu avem acces la datele tale financiare."
              },
              {
                q: "Câte facturi pot trimite gratuit?",
                a: "Planul Gratuit include un număr limitat de facturi pe lună — limita se resetează în fiecare lună. Dacă ai nevoie de mai multe, poți alege un plan superior cu un număr mai mare de facturi. Vezi secțiunea de prețuri pentru detalii."
              },
            ].map((faq, i) => (
              <details key={i} className="group rounded-xl border border-border bg-card hover:border-primary/50 transition-colors">
                <summary className="flex cursor-pointer items-center justify-between p-5 font-heading font-semibold text-sm sm:text-base [&::-webkit-details-marker]:hidden list-none">
                  {faq.q}
                  <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90 shrink-0 ml-4" />
                </summary>
                <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Apps */}
      <section className="py-20 px-4 bg-secondary/30">
        <div className="container max-w-3xl text-center">
          <h2 className="font-heading text-2xl font-bold mb-3">📱 Funcționează și pe Telefon</h2>
          <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
            EZ Factura funcționează direct din browserul telefonului sau tabletei — fără să instalezi nimic. Încarcă o factură, datele se completează automat, și trimiți la ANAF pe loc.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-8">
            <div className="rounded-xl border border-border bg-card p-4 flex flex-col items-center text-center">
              <Upload className="h-5 w-5 text-primary mb-2" />
              <h3 className="font-heading font-semibold text-sm mb-1">Încarcă factura</h3>
              <p className="text-xs text-muted-foreground">Încarcă un PDF sau o imagine și datele se extrag automat.</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 flex flex-col items-center text-center">
              <Smartphone className="h-5 w-5 text-primary mb-2" />
              <h3 className="font-heading font-semibold text-sm mb-1">Interfață adaptată</h3>
              <p className="text-xs text-muted-foreground">Design optimizat pentru ecrane mici — totul e la un tap distanță.</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 flex flex-col items-center text-center">
              <Send className="h-5 w-5 text-primary mb-2" />
              <h3 className="font-heading font-semibold text-sm mb-1">Trimite de oriunde</h3>
              <p className="text-xs text-muted-foreground">Creezi și trimiți factura la ANAF direct de pe telefon, fără laptop.</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <span
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-muted text-muted-foreground px-5 py-3 text-sm font-semibold cursor-not-allowed"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302c.7.4.7 1.08 0 1.48l-2.302 2.302L15.11 12l2.588-2.492zM5.864 2.658L16.8 8.991l-2.302 2.302L5.864 2.658z"/></svg>
              Google Play
            </span>
            <span
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-muted text-muted-foreground px-5 py-3 text-sm font-semibold cursor-not-allowed"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              App Store
            </span>
          </div>
          <p className="text-sm font-semibold text-primary mt-5">⏳ Aplicațiile native sunt în lucru — între timp, totul funcționează perfect din browser!</p>
        </div>
      </section>


      </main>
      <AppFooter />
    </div>
  );
};

export default LandingPage;
